import { useMemo, useState } from 'react';
import type { Offer } from '@data/offers';
import type { PublicPlan } from '@data/publicPlans';
import type { GanttTask } from '../types/gantt';
import type { Gardener } from '@data/gardeners';
import { getPublicPlanById } from '@data/publicPlans';
import { timelineSummaryFromGanttTasks } from '@utils/ganttDataGenerator';
import { getImageUrl } from '@utils/imageUrl';
import GanttChart from './GanttChart';
import { getMockMaterialAvailability } from '@data/mockOfferFeatures';
import { generateOfferPDF } from '@utils/pdfGenerator';
import { useAlertDialog } from '../shared/AlertDialog';
import { useAuth } from '@services/auth';
import { createIssue } from '@services/issues';
import { useTranslatedAIContent } from '../shared/useTranslatedAIContent';
import { useI18n } from '../i18n';

export type OfferDetailPreviewProps = {
	offer: Offer;
	/** Plan for this offer (for fallback when offer has no planSnapshot). Can be from planId. */
	plan: PublicPlan | null;
	/** When 'seeker': "Message from {gardenerName}". When 'sender': "Your message to the seeker". */
	viewAs?: 'seeker' | 'sender';
	/** Plan ID (e.g. from URL) for getPublicPlanById fallback when plan prop is null. */
	planId?: string;
};

function getTimelineSummary(offer: Offer) {
	if (offer.timelineData && offer.timelineData.length > 0) {
		return timelineSummaryFromGanttTasks(offer.timelineData);
	}
	if (offer.timelineSummary) {
		return offer.timelineSummary;
	}
	return null;
}

/** Normalize offer timeline tasks and derive gardeners so Gantt bars resolve assignees (names-only API rows get stable synthetic ids). */
function ganttPropsFromOfferTimeline(tasks: GanttTask[]): { chartTasks: GanttTask[]; gardeners: Gardener[] } {
	const gardenersById = new Map<string, Gardener>();
	const nameKeyToId = new Map<string, string>();

	const ensureSynthId = (displayName: string): string => {
		const name = displayName.trim();
		const key = name.toLowerCase();
		let id = nameKeyToId.get(key);
		if (!id) {
			id = `offer-assignee-${nameKeyToId.size}`;
			nameKeyToId.set(key, id);
			gardenersById.set(id, { id, name });
		}
		return id;
	};

	const chartTasks = tasks.map((t) => {
		const names =
			t.gardenerNames?.length ? t.gardenerNames :
			t.gardenerName ? [t.gardenerName] :
			[];
		let ids = t.gardenerIds?.length ? [...t.gardenerIds] : t.gardenerId ? [t.gardenerId] : [];

		ids.forEach((id, idx) => {
			if (!id || gardenersById.has(id)) return;
			const nm = names[idx] ?? names[0] ?? id;
			gardenersById.set(id, { id, name: String(nm) });
		});

		if (ids.length === 0 && names.length > 0) {
			ids = names.map((n) => ensureSynthId(String(n)));
		}

		return {
			...t,
			progress: typeof t.progress === 'number' ? t.progress : 0,
			...(ids.length > 0 ? { gardenerIds: ids } : {})
		};
	});

	return { chartTasks, gardeners: [...gardenersById.values()] };
}

export default function OfferDetailPreview({
	offer,
	plan,
	viewAs = 'seeker',
	planId
}: OfferDetailPreviewProps) {
	const resolvedPlan = plan ?? (planId ? getPublicPlanById(planId) ?? null : null);
	const timelineSummary = getTimelineSummary(offer);
	const { showAlert } = useAlertDialog();
	const { user } = useAuth();
	const [isDownloadingPdf, setIsDownloadingPdf] = useState(false);
	const [showReportModal, setShowReportModal] = useState(false);
	const [reportReason, setReportReason] = useState<'misleading_details' | 'suspected_scam' | 'inappropriate_content' | 'spam_duplicate' | 'other'>('misleading_details');
	const [reportMessage, setReportMessage] = useState('');
	const [isSubmittingReport, setIsSubmittingReport] = useState(false);
	const { language } = useI18n();
	const copy = (en: string, de: string, hr: string) =>
		language === 'de' ? de : language === 'hr' ? hr : en;
	const locale = language === 'de' ? 'de-DE' : language === 'hr' ? 'hr-HR' : 'en-GB';
	const formatLeadTime = (leadTime?: string | null) => {
		if (!leadTime) return '';
		return leadTime.replace(/\bdays?\b/gi, copy('days', 'Tage', 'dana'));
	};
	const formatTaskStatus = (status: string) => {
		if (status === 'in_progress') return copy('In progress', 'In Arbeit', 'U tijeku');
		if (status === 'completed') return copy('Completed', 'Abgeschlossen', 'Završeno');
		if (status === 'blocked') return copy('Blocked', 'Blockiert', 'Blokirano');
		if (status === 'at_risk') return copy('At risk', 'Gefährdet', 'Rizično');
		if (status === 'delayed') return copy('Delayed', 'Verzögert', 'Odgođeno');
		return status.replace(/_/g, ' ');
	};

	const offerGantt = useMemo(() => {
		if (!offer.timelineData?.length) return null;
		return ganttPropsFromOfferTimeline(offer.timelineData);
	}, [offer.timelineData]);

	const canDownloadPdf = Boolean(resolvedPlan && (offer.planSnapshot?.aiGenerated ?? resolvedPlan.aiGenerated));
	const baseAiContent = (offer.planSnapshot?.aiGenerated ?? resolvedPlan?.aiGenerated) ?? null;
	const { translatedContent: translatedAiContent } = useTranslatedAIContent(baseAiContent);

	async function handleDownloadPdf() {
		if (!resolvedPlan) {
			showAlert(copy('Plan data is not available for PDF generation.', 'Plandaten sind für die PDF-Erstellung nicht verfügbar.', 'Podaci plana nisu dostupni za izradu PDF-a.'), { title: copy('Error', 'Fehler', 'Greška') });
			return;
		}
		if (!canDownloadPdf) {
			showAlert(copy('This offer does not have complete data for PDF generation.', 'Dieses Angebot enthält keine vollständigen Daten für die PDF-Erstellung.', 'Ova ponuda nema potpune podatke za izradu PDF-a.'), { title: copy('Error', 'Fehler', 'Greška') });
			return;
		}

		try {
			setIsDownloadingPdf(true);
			await generateOfferPDF({ plan: resolvedPlan, offer, language });
		} catch (err) {
			console.error('Failed to generate offer PDF:', err);
			showAlert(copy('Failed to generate offer PDF. Please try again.', 'Angebots-PDF konnte nicht erstellt werden. Bitte versuchen Sie es erneut.', 'Izrada PDF-a ponude nije uspjela. Pokušajte ponovno.'), { title: copy('Error', 'Fehler', 'Greška') });
		} finally {
			setIsDownloadingPdf(false);
		}
	}

	async function handleSubmitOfferReport() {
		const userId = user?.id;
		const reporterUserId = typeof userId === 'string' ? parseInt(userId, 10) : userId;
		const offerIdNum = parseInt(offer.id, 10);
		if (!reporterUserId || isNaN(reporterUserId)) {
			showAlert(copy('You must be logged in to report an offer.', 'Sie müssen angemeldet sein, um ein Angebot zu melden.', 'Morate biti prijavljeni da biste prijavili ponudu.'), { title: copy('Sign in required', 'Anmeldung erforderlich', 'Potrebna prijava') });
			return;
		}
		if (isNaN(offerIdNum)) {
			showAlert(copy('This offer cannot be reported yet.', 'Dieses Angebot kann noch nicht gemeldet werden.', 'Ova ponuda se još ne može prijaviti.'), { title: copy('Unavailable', 'Nicht verfügbar', 'Nedostupno') });
			return;
		}

		setIsSubmittingReport(true);
		try {
			await createIssue({
				reporter_user_id: reporterUserId,
				type: 'offer_report',
				category: 'offer',
				target_type: 'offer',
				target_id: offerIdNum,
				reason_code: reportReason,
				message: reportMessage.trim() || null
			});
			setShowReportModal(false);
			setReportMessage('');
			setReportReason('misleading_details');
			showAlert(copy('Thanks, your report has been submitted for review.', 'Danke, Ihre Meldung wurde zur Prüfung eingereicht.', 'Hvala, vaša prijava je poslana na pregled.'), { title: copy('Report submitted', 'Meldung gesendet', 'Prijava poslana') });
		} catch (err) {
			console.error('Failed to submit offer report:', err);
			showAlert(err instanceof Error ? err.message : copy('Failed to submit report. Please try again.', 'Senden der Meldung fehlgeschlagen. Bitte versuchen Sie es erneut.', 'Slanje prijave nije uspjelo. Pokušajte ponovno.'), { title: copy('Error', 'Fehler', 'Greška') });
		} finally {
			setIsSubmittingReport(false);
		}
	}

	return (
		<div className="p-4 bg-light offer-detail-preview">
			{/* Offer details summary */}
			<div className="card border shadow-sm mb-4">
				<div className="card-header bg-white py-2 d-flex align-items-start justify-content-between gap-3">
					<h6 className="mb-0 text-success fw-semibold">
						<i className="bi bi-receipt me-2"></i>{copy('Offer details', 'Angebotsdetails', 'Detalji ponude')}
					</h6>
					<div className="d-flex align-items-center gap-2">
						{user && (
							<button
								type="button"
								className="btn btn-outline-danger btn-sm"
								onClick={() => setShowReportModal(true)}
							>
								<i className="bi bi-flag me-2"></i>
								{copy('Report offer', 'Angebot melden', 'Prijavi ponudu')}
							</button>
						)}
						<button
							type="button"
							className="btn btn-outline-success btn-sm"
							onClick={handleDownloadPdf}
							disabled={!canDownloadPdf || isDownloadingPdf}
							title={canDownloadPdf ? copy('Download offer PDF', 'Angebots-PDF herunterladen', 'Preuzmi PDF ponude') : copy('Offer PDF not available yet', 'Angebots-PDF ist noch nicht verfügbar', 'PDF ponude još nije dostupan')}
						>
							<i className="bi bi-download me-2"></i>
							{copy('Download PDF', 'PDF herunterladen', 'Preuzmi PDF')}
						</button>
					</div>
				</div>
				<div className="card-body py-3">
					<div className="row g-3">
						<div className="col-6 col-md-3">
							<div className="text-muted small text-uppercase">{copy('Estimated price', 'Geschätzter Preis', 'Procijenjena cijena')}</div>
							<div className="fw-semibold">{(offer.bands.mid || offer.bands.min).toLocaleString()} €</div>
						</div>
						{offer.validUntil && (
							<div className="col-6 col-md-3">
								<div className="text-muted small text-uppercase">{copy('Valid until', 'Gültig bis', 'Vrijedi do')}</div>
								<div className="fw-semibold">{new Date(offer.validUntil).toLocaleDateString(locale, { day: 'numeric', month: 'short', year: 'numeric' })}</div>
							</div>
						)}
						{offer.projectStartDate && (
							<div className="col-6 col-md-3">
								<div className="text-muted small text-uppercase">{copy('Project start', 'Projektstart', 'Početak projekta')}</div>
								<div className="fw-semibold">{new Date(offer.projectStartDate).toLocaleDateString(locale, { day: 'numeric', month: 'short', year: 'numeric' })}</div>
							</div>
						)}
						{offer.timelineEstimate?.trim() && (
							<div className="col-12 col-md">
								<div className="text-muted small text-uppercase">{copy('Timeline estimate', 'Zeitplan-Schätzung', 'Procjena vremenskog plana')}</div>
								<div className="fw-semibold">{offer.timelineEstimate.trim()}</div>
							</div>
						)}
					</div>
				</div>
			</div>
			{offer.message?.trim() && (
				<div className="mb-4">
					<h6 className="mb-2">
						{viewAs === 'sender' ? copy('Your message to the seeker', 'Ihre Nachricht an den Suchenden', 'Vaša poruka vlasniku') : `${copy('Message from', 'Nachricht von', 'Poruka od')} ${offer.gardenerName}`}
					</h6>
					<p className="mb-0 text-muted small" style={{ whiteSpace: 'pre-wrap' }}>{offer.message.trim()}</p>
				</div>
			)}
			{offer.selectedGardeners && offer.selectedGardeners.length > 0 && (
				<div className="mb-4">
					<h6 className="mb-2">{copy('Assigned Gardeners', 'Zugewiesene Gärtner', 'Dodijeljeni vrtlari')}</h6>
					<div className="d-flex flex-wrap gap-2">
						{offer.selectedGardeners.map((name, idx) => (
							<span key={idx} className="badge text-bg-success">
								<i className="bi bi-person me-1"></i>
								{name}
							</span>
						))}
					</div>
				</div>
			)}
			{/* Full plan in this offer */}
			{(() => {
				const snapshot = offer.planSnapshot?.aiGenerated;
				const ai = translatedAiContent ?? snapshot ?? resolvedPlan?.aiGenerated;
				const beforeImageUrlRaw =
					offer.planSnapshot?.questionnaire?.photoBeforeUrl ??
					offer.planSnapshot?.photoBeforeUrl ??
					resolvedPlan?.questionnaire?.photoBeforeUrl ??
					resolvedPlan?.photoBeforeUrl ??
					null;
				const afterImageUrlRaw = ai?.photoAfterUrl ?? null;
				const beforeImageUrl = beforeImageUrlRaw ? getImageUrl(beforeImageUrlRaw) : null;
				const afterImageUrl =
					afterImageUrlRaw && !String(afterImageUrlRaw).includes('unsplash.com')
						? getImageUrl(afterImageUrlRaw)
						: null;
				const hasBeforeAfter = Boolean(beforeImageUrl || afterImageUrl);
				if (!ai) {
					return (
						<div className="alert alert-light border mb-4">
							<i className="bi bi-hourglass-split me-2"></i>
							{viewAs === 'sender'
								? copy('Plan details are loading. This offer was sent for a plan from the Marketplace.', 'Plandetails werden geladen. Dieses Angebot wurde für einen Plan aus dem Marketplace gesendet.', 'Detalji plana se učitavaju. Ova ponuda je poslana za plan s Marketplacea.')
								: copy("Plan details are loading. If they don't appear, view your plan from My Plans to see the full description, features, and image.", 'Plandetails werden geladen. Wenn sie nicht erscheinen, öffnen Sie Ihren Plan unter Meine Pläne, um die vollständige Beschreibung, Merkmale und das Bild zu sehen.', 'Detalji plana se učitavaju. Ako se ne prikažu, otvorite plan u Mojim planovima kako biste vidjeli puni opis, značajke i sliku.')}
						</div>
					);
				}
				const isCustomized = Boolean(snapshot);
				return (
					<div className="card border shadow-sm mb-4">
						<div className="card-header bg-success bg-opacity-10 py-2">
							<h6 className="mb-0 text-success fw-semibold">
								<i className="bi bi-file-earmark-text me-2"></i>
								{isCustomized
									? copy('Plan in this offer', 'Plan in diesem Angebot', 'Plan u ovoj ponudi')
									: copy('Plan overview (what this offer is for)', 'Planübersicht (wofür dieses Angebot ist)', 'Pregled plana (za što je ova ponuda)')}
							</h6>
							<p className="small text-muted mb-0 mt-1">
								{isCustomized
									? (viewAs === 'sender'
										? copy('What you proposed in this offer.', 'Was Sie in diesem Angebot vorgeschlagen haben.', 'Što ste predložili u ovoj ponudi.')
										: copy('What this gardener proposed; may differ from your published plan.', 'Was dieser Gärtner vorgeschlagen hat; kann von Ihrem veröffentlichten Plan abweichen.', 'Što je predložio ovaj vrtlar; može se razlikovati od vašeg objavljenog plana.'))
									: (viewAs === 'sender'
										? copy('The plan you quoted — from the Marketplace.', 'Der Plan, den Sie angeboten haben — aus dem Marketplace.', 'Plan koji ste ponudili — s Marketplacea.')
										: copy('Your published plan — this offer is based on it.', 'Ihr veröffentlichter Plan — dieses Angebot basiert darauf.', 'Vaš objavljeni plan — ova ponuda se temelji na njemu.'))}
							</p>
						</div>
						<div className="card-body" style={{ minWidth: 0 }}>
							{hasBeforeAfter && (
								<div className="mb-4">
									<div className="text-muted small mb-2">
										{isCustomized
											? (viewAs === 'sender'
												? copy('Your proposed vision', 'Ihre vorgeschlagene Vision', 'Vaša predložena vizija')
												: copy('Proposed vision', 'Vorgeschlagene Vision', 'Predložena vizija'))
											: copy('Garden vision', 'Gartenvision', 'Vizija vrta')}
									</div>
									<div className="row g-3 offer-vision-images">
										{beforeImageUrl && (
											<div className={afterImageUrl ? 'col-12 col-lg-6' : 'col-12'}>
												<div className="text-muted small mb-1">{copy('Before', 'Vorher', 'Prije')}</div>
												<img
													src={beforeImageUrl}
													alt={copy('Garden before', 'Garten vorher', 'Vrt prije')}
													className="img-fluid rounded border"
												/>
											</div>
										)}
										{afterImageUrl && (
											<div className={beforeImageUrl ? 'col-12 col-lg-6' : 'col-12'}>
												<div className="text-muted small mb-1">
													{isCustomized
														? copy('After (proposed)', 'Nachher (vorgeschlagen)', 'Poslije (prijedlog)')
														: copy('After', 'Nachher', 'Poslije')}
												</div>
												<img
													src={afterImageUrl}
													alt={isCustomized
														? copy('Proposed garden', 'Vorgeschlagener Garten', 'Predloženi vrt')
														: copy('Garden vision', 'Gartenvision', 'Vizija vrta')}
													className="img-fluid rounded border"
												/>
											</div>
										)}
									</div>
								</div>
							)}
							<div className="mb-3">
								<div className="text-muted small text-uppercase mb-1">{copy('Description', 'Beschreibung', 'Opis')}</div>
								<p className="mb-0 small">{ai.planDescription}</p>
							</div>
							{ai.keyFeatures?.length > 0 && (
								<div className="mb-3">
									<div className="text-muted small text-uppercase mb-1">{copy('Key features', 'Hauptmerkmale', 'Ključne značajke')}</div>
									<ul className="small mb-0 ps-3">
										{ai.keyFeatures.map((f: string, idx: number) => (
											<li key={idx}>{f}</li>
										))}
									</ul>
								</div>
							)}
							{ai.instructions?.length > 0 && (
								<div className="mb-3">
									<div className="text-muted small text-uppercase mb-1">{copy('Implementation steps', 'Umsetzungsschritte', 'Koraci izvedbe')}</div>
									<ol className="small mb-0 ps-3">
										{ai.instructions.map((step: string, idx: number) => (
											<li key={idx}>{step}</li>
										))}
									</ol>
								</div>
							)}
							{ai.pricingBreakdown && (
								<div className="mb-3">
									<div className="text-muted small text-uppercase mb-1">{copy('Estimated pricing (plan)', 'Geschätzte Kosten (Plan)', 'Procijenjena cijena (plan)')}</div>
									<div className="small">
										{ai.pricingBreakdown.materials?.length > 0 && (
											<div className="mb-2">
												<span className="fw-medium">{copy('Materials:', 'Materialien:', 'Materijali:')}</span>
												{ai.pricingBreakdown.materials.map((m: { name: string; quantity: number; unit: string; unitPrice: number; total: number }, i: number) => {
													const availability = getMockMaterialAvailability(m.name, m.quantity, m.unit);
													return (
														<div key={i} className="ms-3 text-muted d-flex flex-wrap align-items-center gap-1 mt-1">
															<span>{m.name}: {m.quantity} {m.unit} × {m.unitPrice?.toLocaleString()} € = {m.total?.toLocaleString()} €</span>
															{availability.status === 'in_stock' && <span className="badge text-bg-success small">{copy('In stock', 'Auf Lager', 'Na zalihi')}</span>}
															{availability.status === 'short' && availability.shortBy && (
																<span className="badge text-bg-warning small">{copy('Short by', 'Fehlt um', 'Nedostaje')} {availability.shortBy.quantity} {availability.shortBy.unit}</span>
															)}
															{availability.status === 'out_of_stock' && <span className="badge text-bg-danger small">{copy('Out of stock', 'Nicht auf Lager', 'Nema na zalihi')}</span>}
															{availability.leadTime && <span className="badge bg-secondary bg-opacity-75 small">{formatLeadTime(availability.leadTime)}</span>}
														</div>
													);
												})}
											</div>
										)}
										{ai.pricingBreakdown.labor?.length > 0 && (
											<div>
												<span className="fw-medium">{copy('Labor:', 'Arbeitsleistung:', 'Rad:')}</span>
												{ai.pricingBreakdown.labor.map((l: { name: string; hours: number; ratePerHour: number; total: number }, i: number) => (
													<div key={i} className="ms-3 text-muted">{l.name}: {l.hours} h × {l.ratePerHour?.toLocaleString()} € = {l.total?.toLocaleString()} €</div>
												))}
											</div>
										)}
									</div>
								</div>
							)}
							{ai.tools && (ai.tools as unknown[]).length > 0 && (
								<div>
									<div className="text-muted small text-uppercase mb-1">{copy('Required tools', 'Benötigte Werkzeuge', 'Potrebni alati')}</div>
									<ul className="small mb-0 ps-3">
										{typeof (ai.tools as unknown[])[0] === 'string'
											? (ai.tools as string[]).map((t: string, i: number) => <li key={i}>{t}</li>)
											: (ai.tools as Array<{ name: string; description?: string }>).map((t, i) => (
												<li key={i}><strong>{t.name}</strong>{t.description ? ` — ${t.description}` : ''}</li>
											))}
									</ul>
								</div>
							)}
						</div>
					</div>
				);
			})()}
			{/* Project Timeline */}
			{offer.timelineData && offer.timelineData.length > 0 ? (
				<div className="card border shadow-sm overflow-hidden">
					<div className="card-body p-4">
						<h6 className="mb-1 text-success fw-semibold">
							<i className="bi bi-calendar-range me-2"></i>{copy('Project Timeline', 'Projektzeitplan', 'Vremenski plan projekta')}
						</h6>
						<p className="small text-muted mb-3">
							{viewAs === 'sender'
								? copy('As submitted with this offer.', 'Wie mit diesem Angebot eingereicht.', 'Kao što je poslano s ovom ponudom.')
								: copy('As submitted with this offer by the gardener.', 'Wie vom Gärtner mit diesem Angebot eingereicht.', 'Kao što je vrtlar poslao uz ovu ponudu.')}
						</p>
						{timelineSummary && (
							<div className="row g-2 mb-4">
								<div className="col-6 col-lg-3">
									<div className="border rounded-3 px-3 py-2 h-100">
										<div className="text-muted small text-uppercase">{copy('Duration', 'Dauer', 'Trajanje')}</div>
										<div className="fw-semibold">{timelineSummary.totalDuration}d</div>
									</div>
								</div>
								<div className="col-6 col-lg-3">
									<div className="border rounded-3 px-3 py-2 h-100">
										<div className="text-muted small text-uppercase">{copy('Gardeners', 'Gärtner', 'Vrtlari')}</div>
										<div className="fw-semibold">{timelineSummary.gardenerCount}</div>
									</div>
								</div>
								<div className="col-6 col-lg-3">
									<div className="border rounded-3 px-3 py-2 h-100">
										<div className="text-muted small text-uppercase">{copy('Start', 'Start', 'Početak')}</div>
										<div className="fw-semibold small">
											{new Date(timelineSummary.startDate).toLocaleDateString(locale, { day: 'numeric', month: 'short' })}
										</div>
									</div>
								</div>
								<div className="col-6 col-lg-3">
									<div className="border rounded-3 px-3 py-2 h-100">
										<div className="text-muted small text-uppercase">{copy('Finish', 'Abschluss', 'Završetak')}</div>
										<div className="fw-semibold small">
											{new Date(timelineSummary.endDate).toLocaleDateString(locale, { day: 'numeric', month: 'short' })}
										</div>
									</div>
								</div>
							</div>
						)}
						{offerGantt && (
							<div className="mb-4">
								<h6 className="mb-2 small text-muted text-uppercase">
									{copy('Timeline chart', 'Zeitplan-Diagramm', 'Graf vremenskog plana')}
								</h6>
								<div className="border rounded-3 bg-white overflow-hidden">
									<GanttChart
										tasks={offerGantt.chartTasks}
										gardeners={offerGantt.gardeners}
										startDate={
											timelineSummary
												? new Date(timelineSummary.startDate)
												: undefined
										}
										endDate={
											timelineSummary
												? new Date(timelineSummary.endDate)
												: undefined
										}
										preview
										className="p-2"
									/>
								</div>
							</div>
						)}
						<h6 className="mb-2 small text-muted text-uppercase">{copy('Tasks', 'Aufgaben', 'Zadaci')}</h6>
						<div className="d-flex flex-column gap-2">
							{offer.timelineData.map((task, index) => {
								const status = task.status ?? 'not_started';
								const showStatus = status !== 'not_started';
								return (
									<div key={task.id} className="border rounded-3 bg-white px-3 py-2">
										<div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-1">
											<div className="d-flex align-items-center gap-2">
												<span className="badge text-bg-light border text-muted">#{index + 1}</span>
												<div className="fw-medium text-dark" style={{ lineHeight: 1.3 }}>
													{task.name}
												</div>
											</div>
											<span className="small fw-semibold text-success">{task.duration ?? 0}d</span>
										</div>
										<div className="d-flex flex-wrap align-items-center gap-2 small text-muted">
											<span>
												{new Date(task.startDate).toLocaleDateString(locale, { day: 'numeric', month: 'short' })}
												{' - '}
												{new Date(task.endDate).toLocaleDateString(locale, { day: 'numeric', month: 'short' })}
											</span>
											{showStatus && (
												<span className="badge bg-warning-subtle text-warning-emphasis border text-uppercase">
													{formatTaskStatus(status)}
												</span>
											)}
											{task.gardenerNames && task.gardenerNames.length > 0 && (
												<span>
													{copy('Assigned:', 'Zugewiesen:', 'Dodijeljeno:')} {task.gardenerNames.join(', ')}
												</span>
											)}
										</div>
									</div>
								);
							})}
						</div>
					</div>
				</div>
			) : (offer.timelineEstimate?.trim() || timelineSummary) ? (
				<div className="card border shadow-sm overflow-hidden">
					<div className="card-body p-4">
						<h6 className="mb-3 text-success fw-semibold">
							<i className="bi bi-calendar-range me-2"></i>{copy('Project Timeline', 'Projektzeitplan', 'Vremenski plan projekta')}
						</h6>
						{timelineSummary ? (
							<div className="row g-2">
								<div className="col-6 col-lg-3">
									<div className="border rounded-3 px-3 py-2 h-100">
										<div className="text-muted small text-uppercase">{copy('Duration', 'Dauer', 'Trajanje')}</div>
										<div className="fw-semibold">{timelineSummary.totalDuration}d</div>
									</div>
								</div>
								<div className="col-6 col-lg-3">
									<div className="border rounded-3 px-3 py-2 h-100">
										<div className="text-muted small text-uppercase">{copy('Gardeners', 'Gärtner', 'Vrtlari')}</div>
										<div className="fw-semibold">{timelineSummary.gardenerCount}</div>
									</div>
								</div>
								<div className="col-6 col-lg-3">
									<div className="border rounded-3 px-3 py-2 h-100">
										<div className="text-muted small text-uppercase">{copy('Start', 'Start', 'Početak')}</div>
										<div className="fw-semibold small">
											{new Date(timelineSummary.startDate).toLocaleDateString(locale, { day: 'numeric', month: 'short' })}
										</div>
									</div>
								</div>
								<div className="col-6 col-lg-3">
									<div className="border rounded-3 px-3 py-2 h-100">
										<div className="text-muted small text-uppercase">{copy('Finish', 'Abschluss', 'Završetak')}</div>
										<div className="fw-semibold small">
											{new Date(timelineSummary.endDate).toLocaleDateString(locale, { day: 'numeric', month: 'short' })}
										</div>
									</div>
								</div>
							</div>
						) : (
							<p className="mb-0">{offer.timelineEstimate?.trim()}</p>
						)}
					</div>
				</div>
			) : (
				<div className="text-muted small py-2">
					<i className="bi bi-info-circle me-1"></i>{copy('No timeline provided with this offer.', 'Kein Zeitplan in diesem Angebot enthalten.', 'Nema vremenskog plana u ovoj ponudi.')}
				</div>
			)}
			{showReportModal && (
				<div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.45)' }} role="dialog" aria-modal="true" aria-labelledby="reportOfferTitle">
					<div className="modal-dialog modal-dialog-centered">
						<div className="modal-content">
							<div className="modal-header">
								<h5 className="modal-title" id="reportOfferTitle">{copy('Report offer', 'Angebot melden', 'Prijavi ponudu')}</h5>
								<button type="button" className="btn-close" aria-label="Close" onClick={() => setShowReportModal(false)} disabled={isSubmittingReport} />
							</div>
							<div className="modal-body">
								<p className="text-muted small mb-3">
									Help us keep the marketplace safe. Choose the best reason and include details if needed.
								</p>
								<label className="form-label small text-uppercase text-muted">{copy('Reason', 'Grund', 'Razlog')}</label>
								<select
									className="form-select mb-3"
									value={reportReason}
									onChange={(e) => setReportReason(e.target.value as typeof reportReason)}
									disabled={isSubmittingReport}
								>
									<option value="misleading_details">{copy('Misleading details or pricing', 'Irreführende Details oder Preise', 'Obmanjujući detalji ili cijena')}</option>
									<option value="suspected_scam">{copy('Suspected scam', 'Vermuteter Betrug', 'Sumnja na prijevaru')}</option>
									<option value="inappropriate_content">{copy('Inappropriate content', 'Unangemessene Inhalte', 'Neprimjeren sadržaj')}</option>
									<option value="spam_duplicate">{copy('Spam or duplicate offer', 'Spam oder doppeltes Angebot', 'Spam ili duplikat ponude')}</option>
									<option value="other">{copy('Other', 'Sonstiges', 'Ostalo')}</option>
								</select>
								<label className="form-label small text-uppercase text-muted">{copy('Additional details (optional)', 'Zusätzliche Details (optional)', 'Dodatni detalji (opcionalno)')}</label>
								<textarea
									className="form-control"
									rows={4}
									maxLength={1200}
									value={reportMessage}
									onChange={(e) => setReportMessage(e.target.value)}
									placeholder="Describe what looks wrong in this offer..."
									disabled={isSubmittingReport}
								/>
							</div>
							<div className="modal-footer">
								<button type="button" className="btn btn-outline-secondary" onClick={() => setShowReportModal(false)} disabled={isSubmittingReport}>
									Cancel
								</button>
								<button type="button" className="btn btn-danger" onClick={handleSubmitOfferReport} disabled={isSubmittingReport}>
									{isSubmittingReport ? 'Submitting…' : 'Submit report'}
								</button>
							</div>
						</div>
					</div>
				</div>
			)}
		</div>
	);
}
